import { DEVICECOW_TOOL_CATALOG } from "./devicecow-tools.js";
/** Application tool permissions. Unknown IDs fail closed in every runtime. */
export type ToolRuntime = "aquaduck" | "devicecow";
export interface ToolOverride { enabled?: boolean; config?: { model?: string; maxOutputTokens?: number } }
export type ToolOverrides = Record<string, ToolOverride>;
export interface ToolEntry {
  id: string; label: string; description: string; runtimes: readonly ToolRuntime[];
  defaultEnabled: boolean; required?: boolean; dependencies: readonly string[];
  configSchema: { type: "object"; additionalProperties: false; properties: Record<string, unknown> };
}
const entries: [string, string, string, string[]?][] = [
  ["forward_to_devicecow", "Forward to Device Cow", "Forward approved application development requests."],
  ["list_users", "List people", "Read workspace people and presence."],
  ["read_thread", "Read comment threads", "Read comments and replies."],
  ["reply_in_thread", "Reply in threads", "Deliver replies to the requesting conversation."],
  ["read_chat", "Read chat", "Read recent messages in the current conversation."],
  ["read_chat_attachment", "Read attachments", "Read bounded text from conversation attachments."],
  ["list_objects", "List objects", "Read object metadata and short excerpts."],
  ["search_objects", "Search objects", "Search live workspace objects."],
  ["generate_image", "Generate images", "Create images using the image provider."],
  ["begin_object", "Reserve output", "Manage visible reservations for permitted output types."],
  ["read_template", "Read templates", "Read typography, palettes and layout guidance."],
  ["read_object", "Read objects", "Read text, file metadata and presentation outlines."],
  ["describe_image", "Inspect images", "Ask the coordinating model about image pixels.", ["read_object"]],
  ["create_frame", "Create frames", "Organize objects in canvas folders."],
  ["move_to_frame", "Move into frames", "Change canvas folder membership."],
  ["create_presentation", "Create presentations", "Save slide presentations and their evidence."],
  ["update_presentation", "Edit presentations", "Update presentations with conflict detection.", ["read_object", "read_project_file"]],
  ["list_project_files", "List project files", "List authored HTML and presentation files."],
  ["read_project_file", "Read project files", "Read authored HTML and presentation source."],
  ["update_project_files", "Edit project files", "Update authored files atomically.", ["read_project_file"]],
  ["create_text_object", "Create text", "Save Markdown as rich text."],
  ["update_text_object", "Edit text", "Revise existing rich text.", ["read_object"]],
  ["render_markdown_report", "Render reports", "Copy rich text into an HTML report.", ["read_object", "create_html_object"]],
  ["create_html_object", "Create HTML", "Create interactive HTML artifacts."],
  ["update_html_object", "Edit HTML", "Update existing HTML artifacts.", ["read_project_file"]],
  ["update_object", "Edit object properties", "Change titles, positions, sizes and colors."],
  ["delete_object", "Delete objects", "Delete objects when the requesting human explicitly asks."],
  ["forward_to_present", "Add to Present", "Link a canvas object into Present."],
  ["remove_from_present", "Remove from Present", "Remove a linked Present block."],
  ["reorder_present", "Reorder Present", "Change Present block order."],
  ["update_present_block", "Edit Present blocks", "Edit Present titles, text and appearance."],
  ["list_present", "Read Present", "List Present blocks in order."],
  ["add_comment", "Add comments", "Add anchored workspace comments."],
  ["post_chat", "Reply in chat", "Deliver results to the requesting conversation."],
  ["set_status", "Working status", "Publish progress while working."],
  ["creative_write", "Gemini writing", "Draft or revise presentation copy and other prose with Gemini through Vertex AI.", ["read_object"]],
];
export const TOOL_CATALOG: readonly ToolEntry[] = entries.map(([id, label, description, dependencies = []]) => ({
  id, label, description, runtimes: ["aquaduck"], dependencies,
  defaultEnabled: id !== "creative_write",
  ...(["begin_object", "post_chat", "reply_in_thread", "set_status"].includes(id) ? { required: true } : {}),
  configSchema: { type: "object", additionalProperties: false, properties: id === "creative_write" ? {
    model: { type: "string", minLength: 1, maxLength: 100 }, maxOutputTokens: { type: "integer", minimum: 256, maximum: 8192 },
  } : {} },
}));
export const AGENT_TOOLS_PATH = "/api/agent/tools";
export interface ToolDeployment {
  writingEnabled: boolean; writingModels: string[]; writingModel: string;
  unavailable?: Record<string, string>;
}
export interface AgentToolsResponse { catalog: readonly ToolEntry[]; deployment: ToolDeployment; runtime: ToolRuntime }
export interface EffectiveTool extends ToolEntry { enabled: boolean; reason?: string; config: { model?: string; maxOutputTokens?: number } }
const record = (value: unknown): value is Record<string, unknown> => !!value && typeof value === "object" && !Array.isArray(value);
/** Structural guard for editors; individual entries are validated before saving. */
export const isToolOverridesMap = (value: unknown): value is ToolOverrides => record(value);
export function validToolOverride(id: string, value: unknown): value is ToolOverride {
  if (![...TOOL_CATALOG, ...DEVICECOW_TOOL_CATALOG].some(entry => entry.id === id) || !record(value) || Object.keys(value).some(k => k !== "enabled" && k !== "config")) return false;
  if (value.enabled !== undefined && typeof value.enabled !== "boolean") return false;
  if (value.config === undefined) return true;
  if (!record(value.config)) return false;
  const allowed = id === "creative_write" ? ["model", "maxOutputTokens"] : [];
  if (Object.keys(value.config).some(k => !allowed.includes(k))) return false;
  const { model, maxOutputTokens } = value.config;
  return (model === undefined || (typeof model === "string" && /^[a-zA-Z0-9._-]{1,100}$/.test(model))) &&
    (maxOutputTokens === undefined || (typeof maxOutputTokens === "number" && Number.isInteger(maxOutputTokens) && maxOutputTokens >= 256 && maxOutputTokens <= 8192));
}
export function resolveToolPolicy(overrides: unknown, deployment: ToolDeployment, runtime: ToolRuntime = "aquaduck", inherited?: readonly string[]): EffectiveTool[] {
  const raw = record(overrides) ? overrides : {};
  const invalid = overrides !== undefined && !record(overrides);
  const catalog = runtime === "devicecow" ? [...DEVICECOW_TOOL_CATALOG, ...TOOL_CATALOG.filter(tool => !DEVICECOW_TOOL_CATALOG.some(other => other.id === tool.id))] : TOOL_CATALOG;
  const result = catalog.map(entry => {
    const value = raw[entry.id];
    const valid = !invalid && (value === undefined || validToolOverride(entry.id, value));
    const override = valid && record(value) ? value as ToolOverride : {};
    const config = { ...(entry.id === "creative_write" ? { model: deployment.writingModel, maxOutputTokens: 8192 } : {}), ...override.config };
    const reason = !entry.runtimes.includes(runtime) ? "Unavailable in this runtime" :
      deployment.unavailable?.[entry.id] ??
      (entry.id === "creative_write" && !deployment.writingEnabled ? "Vertex writing is not enabled in this deployment" :
      entry.id === "creative_write" && (!config.model || !deployment.writingModels.includes(config.model)) ? "Choose a server-approved Gemini model" :
      !entry.required && !valid ? "Invalid tool configuration" :
      inherited && !inherited.includes(entry.id) ? "Disabled by the owning agent at delegation" :
      !entry.required && !(override.enabled ?? entry.defaultEnabled) ? "Disabled for this agent" : undefined);
    return { ...entry, config, enabled: !reason, ...(reason ? { reason } : {}) };
  });
  // Monotone fixed point supports transitive dependencies without allowing cycles to enable tools.
  for (let pass = 0; pass < result.length; pass++) {
    let changed = false;
    for (const tool of result) if (tool.enabled) {
      const missing = tool.dependencies.find(id => !result.find(other => other.id === id)?.enabled);
      if (missing) { tool.enabled = false; tool.reason = `Requires ${TOOL_CATALOG.find(t => t.id === missing)?.label ?? missing}`; changed = true; }
    }
    if (!changed) break;
  }
  return result;
}
export const creationTool: Readonly<Record<string, string>> = { text: "create_text_object", html: "create_html_object", image: "generate_image", presentation: "create_presentation" };
